import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation, AnimatePresence, useInView } from 'framer-motion';
import { 
  ChevronDownIcon, 
  CursorClickIcon, 
  LightningBoltIcon, 
  ChartBarIcon, 
  FilterIcon,
  CashIcon, 
  LocationMarkerIcon,
  MenuIcon,
  XIcon,
  CheckCircleIcon,
  UserGroupIcon,
  CubeTransparentIcon,
  ShieldCheckIcon,
  BadgeCheckIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  EyeIcon,
  PhotographIcon,
  ClockIcon,
  ArrowRightIcon,
  PlusIcon,
  ChevronRightIcon,
  SearchIcon,
  CalendarIcon,
  ChartPieIcon,
  CreditCardIcon,
  UserIcon,
  DeviceMobileIcon,
  GlobeAltIcon
} from '@heroicons/react/outline';

// Composant principal de la Landing Page
const PartenaireSection = () => {
  const [scrollY, setScrollY] = useState(0);
    const [navbarOpen, setNavbarOpen] = useState(false);
    const [isNavTransparent, setIsNavTransparent] = useState(true);
    const [activeSlide, setActiveSlide] = useState(0);
    
    // Références pour l'animation au scroll
    const heroRef = useRef(null);
    const statsRef = useRef(null);
    const featuresRef = useRef(null);
    const platformRef = useRef(null);
    const screensRef = useRef(null);
    const partnersRef = useRef(null);
    const testimonialsRef = useRef(null);
    
    // InView Hooks pour déclencher les animations
    const isHeroInView = useInView(heroRef, { once: false });
    const isStatsInView = useInView(statsRef, { once: false });
    const isFeaturesInView = useInView(featuresRef, { once: false });
    const isPlatformInView = useInView(platformRef, { once: false });
    const isScreensInView = useInView(screensRef, { once: false });
    const isPartnersInView = useInView(partnersRef, { once: false });
    const isTestimonialsInView = useInView(testimonialsRef, { once: false });
    
    // Animations contrôlées
    const heroControls = useAnimation();
    const statsControls = useAnimation();
    const featuresControls = useAnimation();
    const platformControls = useAnimation();
    const screensControls = useAnimation();
    const partnersControls = useAnimation();
    const testimonialsControls = useAnimation();
    
    useEffect(() => {
      const handleScroll = () => {
        setScrollY(window.scrollY);
        setIsNavTransparent(window.scrollY < 50);
      };
      
      window.addEventListener('scroll', handleScroll);
      
      // Timer pour slideshow de la Hero Section
      const slideshowTimer = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % 3);
      }, 5000);
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
        clearInterval(slideshowTimer);
      };
    }, []);
    
    // Effet pour les animations au scroll
    useEffect(() => {
      if (isHeroInView) heroControls.start("visible");
      if (isStatsInView) statsControls.start("visible");
      if (isFeaturesInView) featuresControls.start("visible");
      if (isPlatformInView) platformControls.start("visible");
      if (isScreensInView) screensControls.start("visible");
      if (isPartnersInView) partnersControls.start("visible");
      if (isTestimonialsInView) testimonialsControls.start("visible");
    }, [
      isHeroInView, isStatsInView, isFeaturesInView, isPlatformInView, 
      isScreensInView, isPartnersInView, isTestimonialsInView,
      heroControls, statsControls, featuresControls, platformControls, 
      screensControls, partnersControls, testimonialsControls
    ]);

  
    // Effets d'animation Framer Motion
    const fadeInUp = {
      hidden: { opacity: 0, y: 30 },
      visible: { 
        opacity: 1, 
        y: 0,
        transition: { 
          duration: 0.8,
          ease: "easeOut"
        }
      }
    };
    
    const staggerContainer = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.1
        }
      }
    };
    
    const scaleIn = {
      hidden: { opacity: 0, scale: 0.8 },
      visible: { 
        opacity: 1, 
        scale: 1,
        transition: { duration: 0.6 }
      }
    };
    
    const slideInLeft = {
      hidden: { opacity: 0, x: -50 },
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.7 }
      }
    };
    
    const slideInRight = {
      hidden: { opacity: 0, x: 50 },
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.7 }
      }
    };

   return (
      <div className="min-h-screen bg-gray-50">

        {/* Section Partenariat - Design moderne inspiré des landing pages premium */}
              <section 
                ref={partnersRef}
                className="py-24 px-4 bg-white relative overflow-hidden" 
                id="partenariat"
              >
                {/* Éléments décoratifs de fond */}
                <div className="absolute right-0 top-0 h-64 w-64 bg-orange-50 rounded-bl-full opacity-70 -z-10"></div>
                <div className="absolute left-0 bottom-0 h-80 w-80 bg-gray-50 rounded-tr-full opacity-80 -z-10"></div>
                
                <div className="max-w-7xl mx-auto relative">
                  <motion.div 
                    className="text-center mb-16"
                    initial="hidden"
                    animate={partnersControls}
                    variants={staggerContainer}
                  >
                    <motion.div variants={scaleIn} className="inline-block bg-orange-100 text-[#E84E0E] text-sm font-semibold px-4 py-1 rounded-full mb-4">
                      DEVENEZ PARTENAIRE
                    </motion.div>
                    <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">
                      Monétisez vos espaces inutilisés
                    </motion.h2>
                    <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-3xl mx-auto">
                      Vous possédez un emplacement stratégique ? Rejoignez notre réseau et transformez vos espaces en source de revenus passifs
                    </motion.p>
                  </motion.div>
                  
                  {/* Carte principale du partenariat - Design inspiré des présentations business premium */}
                  <motion.div 
                    className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-24"
                    initial="hidden"
                    animate={partnersControls}
                    variants={staggerContainer}
                  >
                    {/* Image avec overlay informatif */}
                    <motion.div 
                      className="lg:col-span-5 rounded-2xl overflow-hidden shadow-2xl relative h-full min-h-[400px] lg:min-h-[600px]"
                      variants={slideInLeft}
                    >
                      <img 
                        src="https://images.unsplash.com/photo-1577412647305-991150c7d163?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
                        alt="Espace commercial moderne avec écran digital" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent"></div>
                      
                      <div className="absolute inset-x-0 bottom-0 p-8 text-white">
                        <h3 className="text-3xl font-bold mb-4">
                          <span className="block">Générez des</span>
                          <span className="block">revenus <span className="text-[#E84E0E]">passifs</span></span>
                        </h3>
                        <p className="text-lg opacity-90 mb-6">Transformez vos murs et espaces inutilisés en source de revenus constants et prévisibles</p>
                        
                        {/* Indicateurs de performance */}
                        <div className="grid grid-cols-3 gap-4 mb-6">
                          {[
                            { value: "20%", label: "Taux de commission moyen" },
                            { value: "+10%", label: "Bonus trimestriel de performance" },
                            { value: "24/7", label: "Support dédié partenaires" }
                          ].map((stat, i) => (
                            <div key={i} className="text-center bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-3 border border-white border-opacity-20">
                              <p className="text-2xl font-bold">{stat.value}</p>
                              <p className="text-xs mt-1 text-white text-opacity-80">{stat.label}</p>
                            </div>
                          ))}
                        </div>
                        
                        <button className="bg-white text-[#E84E0E] py-3 px-6 rounded-lg font-bold hover:bg-opacity-90 transition-colors flex items-center shadow-lg">
                          <span>Découvrir les avantages</span>
                          <ChevronRightIcon className="h-5 w-5 ml-1" />
                        </button>
                      </div>
                    </motion.div>
                    
                    {/* Contenu informatif */}
                    <motion.div 
                      className="lg:col-span-7 flex flex-col justify-center"
                      variants={slideInRight}
                    >
                      <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">Un partenariat gagnant-gagnant</h3>
                      <p className="text-lg text-gray-600 mb-10">
                        Notre modèle de partenariat est conçu pour créer une relation mutuellement bénéfique. 
                        Nous prenons en charge tous les aspects techniques et vous proposons une transparence totale 
                        sur les revenus générés par les écrans installés dans vos espaces.
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                        {[
                          {
                            icon: <SparklesIcon className="h-6 w-6 text-white" />,
                            color: "bg-gradient-to-r from-[#E84E0E] to-orange-600",
                            title: "Revenus passifs",
                            description: "Pourcentage fixe sur chaque diffusion sur votre emplacement, entre 15% et 25% selon la localisation"
                          },
                          {
                            icon: <CurrencyDollarIcon className="h-6 w-6 text-white" />,
                            color: "bg-gradient-to-r from-green-500 to-green-600",
                            title: "Bonus de performance",
                            description: "Bonus trimestriel basé sur le taux d'occupation des écrans, jusqu'à 10% supplémentaires"
                          },
                          {
                            icon: <UserGroupIcon className="h-6 w-6 text-white" />,
                            color: "bg-gradient-to-r from-blue-500 to-blue-600",
                            title: "Bonus de recommandation",
                            description: "Commission majorée lorsque vous apportez directement des annonceurs à la plateforme (+5% à +10%)"
                          },
                          {
                            icon: <ShieldCheckIcon className="h-6 w-6 text-white" />,
                            color: "bg-gradient-to-r from-purple-500 to-purple-600",
                            title: "Contrôle du contenu",
                            description: "Validation préalable des types de campagnes pouvant être diffusées sur vos écrans"
                          }
                        ].map((benefit, index) => (
                          <motion.div 
                            key={index} 
                            className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 + index * 0.2 }}
                            whileHover={{ y: -5, boxShadow: "0 20px 30px -10px rgba(0, 0, 0, 0.1)" }}
                          >
                            <div className={`h-12 w-12 ${benefit.color} rounded-xl shadow-lg flex items-center justify-center mb-5`}>
                              {benefit.icon}
                            </div>
                            <h4 className="font-bold text-lg mb-3 text-gray-900">{benefit.title}</h4>
                            <p className="text-gray-600 text-sm">{benefit.description}</p>
                          </motion.div>
                        ))}
                      </div>
                      
                      {/* Simulateur de revenus */}
                      <motion.div 
                        className="bg-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.7 }}
                      >
                        <h4 className="font-bold text-lg mb-4 text-gray-900">Simulateur de revenus partenaire</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          <div>
                            <label className="block text-sm text-gray-600 mb-2 font-medium">Surface disponible (m²)</label>
                            <div className="relative">
                              <input 
                                type="number" 
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent" 
                                defaultValue="10"
                              />
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500">
                                m²
                              </div>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-600 mb-2 font-medium">Passage quotidien estimé</label>
                            <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent appearance-none bg-white">
                              <option>Faible (100-500)</option>
                              <option selected>Moyen (500-2000)</option>
                              <option>Élevé (2000+)</option>
                            </select>
                          </div>
                        </div>
                        <div className="bg-gradient-to-r from-[#E84E0E] to-orange-600 text-white p-6 rounded-xl shadow-lg">
                          <div className="flex justify-between items-center mb-2">
                            <p className="font-medium">Estimation mensuelle</p>
                            <span className="text-xs py-1 px-3 bg-white bg-opacity-20 rounded-full">
                              Commission de 20%
                            </span>
                          </div>
                          <p className="text-3xl font-bold">150 000 - 300 000 FCFA</p>
                          <div className="mt-3 text-sm text-white text-opacity-80">
                            Ces estimations peuvent varier selon l'emplacement exact et la saisonnalité
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  </motion.div>
                  
                  {/* Processus en 4 étapes - Design inspiré des roadmaps sur Dribbble */}
                  <motion.div
                    className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                  >
                    <div className="bg-gray-50 px-8 py-6 border-b border-gray-100">
                      <h3 className="text-2xl font-bold text-gray-900">Comment ça marche ?</h3>
                      <p className="text-gray-600">Notre processus de partenariat en 4 étapes simples</p>
                    </div>
                    
                    <div className="p-8">
                      <div className="relative">
                        {/* Ligne de connexion - pour desktop */}
                        <div className="hidden md:block absolute top-16 left-[60px] right-[60px] h-1 bg-gradient-to-r from-[#E84E0E] via-blue-500 to-green-500 rounded-full z-0"></div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative z-10">
                          {[
                            {
                              step: "01",
                              title: "Contact",
                              description: "Prenez contact avec notre équipe partenariat pour discuter de votre emplacement",
                              icon: <UserGroupIcon className="h-8 w-8 text-white" />,
                              color: "from-[#E84E0E] to-orange-600"
                            },
                            {
                              step: "02",
                              title: "Évaluation",
                              description: "Nos experts évaluent le potentiel de votre emplacement et la faisabilité technique",
                              icon: <ChartBarIcon className="h-8 w-8 text-white" />,
                              color: "from-blue-500 to-blue-600"
                            },
                            {
                              step: "03",
                              title: "Déploiement",
                              description: "Installation professionnelle de l'écran et configuration de votre espace partenaire",
                              icon: <CubeTransparentIcon className="h-8 w-8 text-white" />,
                              color: "from-purple-500 to-purple-600"
                            },
                            {
                              step: "04",
                              title: "Monétisation",
                              description: "Commencez à percevoir des revenus dès la mise en service de l'écran",
                              icon: <CurrencyDollarIcon className="h-8 w-8 text-white" />,
                              color: "from-green-500 to-green-600"
                            }
                          ].map((step, index) => (
                            <motion.div 
                              key={index} 
                              className="flex flex-col items-center text-center relative"
                              initial={{ opacity: 0, y: 20 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.5, delay: 0.2 + index * 0.15 }}
                            >
                              <div className="mb-6 w-16 h-16 bg-gradient-to-r rounded-full flex items-center justify-center shadow-lg relative z-10"
                                   style={{ backgroundImage: `linear-gradient(to right, ${step.color})` }}>
                                {step.icon}
                              </div>
                              <div className="text-sm font-bold text-[#E84E0E] mb-2">ÉTAPE {step.step}</div>
                              <h4 className="text-xl font-bold mb-3 text-gray-900">{step.title}</h4>
                              <p className="text-gray-600">{step.description}</p>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-center">
                      <motion.button 
                        className="flex items-center text-[#E84E0E] font-semibold group"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span>Télécharger le guide complet du partenariat</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 group-hover:translate-y-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </motion.button>
                    </div>
                  </motion.div>
                </div>
              </section>
        
              {/* Section Témoignages - Design inspiré des cards modernes */}
              <section 
                ref={testimonialsRef}
                className="py-24 px-4 bg-gray-50 relative overflow-hidden" 
              >
                {/* Éléments décoratifs */}
                <div className="absolute -left-20 top-0 h-40 w-40 bg-[#E84E0E] opacity-5 rounded-full blur-3xl"></div>
                <div className="absolute right-0 bottom-0 h-40 w-40 bg-blue-500 opacity-5 rounded-full blur-3xl"></div>
                
                <div className="max-w-7xl mx-auto relative">
                  <motion.div 
                    className="text-center mb-16"
                    initial="hidden"
                    animate={testimonialsControls}
                    variants={staggerContainer}
                  >
                    <motion.div variants={scaleIn} className="inline-block bg-orange-100 text-[#E84E0E] text-sm font-semibold px-4 py-1 rounded-full mb-4">
                      TÉMOIGNAGES
                    </motion.div>
                    <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">
                      Ce que disent nos clients
                    </motion.h2>
                    <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-3xl mx-auto">
                      Découvrez l'expérience des annonceurs et partenaires qui nous font confiance au quotidien
                    </motion.p>
                  </motion.div>
                  
                  {/* Grille de témoignages */}
                  <motion.div 
                    className="grid grid-cols-1 md:grid-cols-3 gap-8"
                    initial="hidden"
                    animate={testimonialsControls}
                    variants={staggerContainer}
                  >
                    {[
                      {
                        quote: "La plateforme DOOH d'Accent Media a transformé notre approche publicitaire. L'interface intuitive nous permet de gérer nos campagnes en toute autonomie.",
                        author: "Marie Dupont",
                        position: "Directrice Marketing",
                        company: "Retail Premium",
                        avatar: "MD"
                      },
                      {
                        quote: "Nous avons constaté une augmentation de 40% de notre trafic en magasin durant les périodes de diffusion sur le réseau d'Accent Media.",
                        author: "Thomas Leroy",
                        position: "CEO",
                        company: "TechStore",
                        avatar: "TL"
                      },
                      {
                        quote: "En tant que partenaire, j'apprécie particulièrement la transparence du système et la régularité des revenus générés par les écrans installés dans nos locaux.",
                        author: "Sophie Martin",
                        position: "Gérante",
                        company: "Centre Commercial Elite",
                        avatar: "SM"
                      }
                    ].map((testimonial, index) => (
                      <motion.div 
                        key={index}
                        className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 relative"
                        variants={scaleIn}
                        transition={{ delay: index * 0.2 }}
                        whileHover={{ y: -10 }}
                      >
                        {/* Guillemets décoratifs */}
                        <div className="absolute -top-5 -left-2 text-8xl text-gray-100 opacity-50">"</div>
                        
                        <div className="relative">
                          <div className="mb-6">
                            <div className="flex mb-4">
                              {[...Array(5)].map((_, i) => (
                                <svg key={i} className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                            <p className="text-gray-700 italic">{testimonial.quote}</p>
                          </div>
                          
                          <div className="flex items-center">
                            <div className="h-12 w-12 rounded-full bg-gradient-to-r from-[#E84E0E] to-orange-600 flex items-center justify-center text-white font-bold mr-4">
                              {testimonial.avatar}
                            </div>
                            <div>
                              <p className="font-bold text-gray-900">{testimonial.author}</p>
                              <p className="text-sm text-gray-600">{testimonial.position}, {testimonial.company}</p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                  
                  {/* Bannière de stats inspirée des dashboards */}
                  <motion.div 
                    className="mt-20 bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                      {[
                        { value: "200+", label: "Clients actifs", icon: <UserIcon className="h-6 w-6 text-[#E84E0E]" /> },
                        { value: "95%", label: "Taux de satisfaction", icon: <BadgeCheckIcon className="h-6 w-6 text-[#E84E0E]" /> },
                        { value: "30k+", label: "Campagnes diffusées", icon: <LightningBoltIcon className="h-6 w-6 text-[#E84E0E]" /> },
                        { value: "45+", label: "Partenaires d'affichage", icon: <UserGroupIcon className="h-6 w-6 text-[#E84E0E]" /> }
                      ].map((stat, index) => (
                        <motion.div 
                          key={index} 
                          className="flex items-center space-x-4"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                        >
                          <div className="p-3 bg-orange-100 rounded-xl">
                            {stat.icon}
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                            <p className="text-gray-600">{stat.label}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </section>
        
              {/* Section CTA principale - Design inspiré des CTA premium sur Dribbble */}
              <section className="py-20 px-4 relative">
                <div className="max-w-7xl mx-auto relative z-10">
                  <motion.div
                    className="bg-gradient-to-br from-gray-900 to-black rounded-3xl overflow-hidden shadow-2xl"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                  >
                    {/* Éléments graphiques abstraits */}
                    <div className="absolute inset-0 overflow-hidden">
                      {/* Formes géométriques */}
                      <motion.div 
                        className="absolute top-0 left-0 w-full h-full opacity-10"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 180, repeat: Infinity, ease: "linear" }}
                      >
                        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                          <path 
                            fill="#E84E0E" 
                            d="M48.8,-58.2C63.9,-49,77.3,-33.9,81.6,-16.5C85.9,0.9,81.2,20.5,70.6,35C60.1,49.5,43.8,58.9,26.9,62.8C10,66.7,-7.5,65.1,-25.5,60.3C-43.6,55.6,-62.2,47.6,-70.9,33.3C-79.5,19.1,-78.2,-1.3,-71.2,-18.9C-64.2,-36.4,-51.5,-51.2,-37,-59.8C-22.5,-68.5,-6.2,-71,9.5,-82.2C25.2,-93.4,50.5,-113.4,51.2,-104.5C51.8,-95.6,27.9,-57.8,21.7,-50.2C15.6,-42.5,33.6,-67.4,48.8,-58.2Z" 
                            transform="translate(100 100)" 
                          />
                        </svg>
                      </motion.div>
                      
                      {/* Cercle lumineux */}
                      <motion.div 
                        className="absolute -bottom-20 -right-20 h-80 w-80 bg-[#E84E0E] rounded-full opacity-10 blur-3xl"
                        animate={{ 
                          scale: [1, 1.2, 1],
                          opacity: [0.1, 0.15, 0.1]
                        }}
                        transition={{ 
                          duration: 8,
                          repeat: Infinity,
                          repeatType: "reverse"
                        }}
                      />
                      
                      {/* Lignes de grille */}
                      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zMCAzMGgxdjFoLTF6bTEgMGgxdjFoLTF6bS0yIDBoMXYxaC0xem0tMjkgMGgxdjFoLTF6bTMwLTFoMXYxaC0xeiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIgZmlsbC1ydWxlPSJub256ZXJvIi8+PC9nPjwvc3ZnPg==')] bg-[length:30px_30px] opacity-10"></div>
                    </div>
                    
                    {/* Contenu principal */}
                    <div className="relative z-10 p-12 md:p-16 lg:p-20">
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7">
                          <motion.div 
                            className="inline-block bg-white bg-opacity-10 px-4 py-1 rounded-full mb-6 backdrop-blur-sm"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                          >
                            <span className="text-white text-sm font-semibold">LANCEZ VOTRE CAMPAGNE</span>
                          </motion.div>
                          
                          <motion.h2 
                            className="text-3xl md:text-5xl font-bold mb-6 text-white leading-tight"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                          >
                            Prêt à captiver votre audience avec un impact visuel maximal ?
                          </motion.h2>
                          
                          <motion.p 
                            className="text-lg md:text-xl text-white text-opacity-90 mb-10 max-w-2xl"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                          >
                            Rejoignez plus de 200 annonceurs qui ont déjà adopté notre plateforme et faites briller votre marque sur nos écrans premium
                          </motion.p>
                          
                          <motion.div 
                            className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                          >
                            <motion.button 
                              className="bg-white text-gray-900 font-bold py-4 px-8 rounded-xl shadow-xl hover:shadow-2xl transition duration-300 ease-in-out flex items-center justify-center"
                              whileHover={{ scale: 1.05, boxShadow: "0 25px 50px -12px rgba(255, 255, 255, 0.35)" }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <span>Créer un compte</span>
                              <ChevronRightIcon className="h-5 w-5 ml-2" />
                            </motion.button>
                            
                            <motion.button 
                              className="bg-transparent text-white border-2 border-white hover:bg-white hover:bg-opacity-10 font-bold py-4 px-8 rounded-xl shadow-lg transition duration-300 ease-in-out flex items-center justify-center"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <span>Demander une démo</span>
                            </motion.button>
                          </motion.div>
                        </div>
                        
                        <div className="lg:col-span-5">
                          <motion.div 
                            className="bg-white bg-opacity-5 backdrop-blur-sm rounded-2xl p-8 border border-white border-opacity-10 shadow-2xl"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            
                            animate={{ 
                              y: [0, -10, 0],
                            }}
                            transition={{ 
                              y: {
                                duration: 6,
                                repeat: Infinity,
                                repeatType: "reverse"
                              }
                            }}
                          >
                            <div className="flex items-center mb-6">
                              <div className="h-12 w-12 bg-[#E84E0E] rounded-xl flex items-center justify-center mr-4 shadow-lg">
                                <BadgeCheckIcon className="h-7 w-7 text-white" />
                              </div>
                              <div>
                                <h3 className="font-bold text-xl text-white">Résultats prouvés</h3>
                                <p className="text-white text-opacity-80 text-sm">Impact mesurable et garanti</p>
                              </div>
                            </div>
                            
                            <div className="space-y-4 mb-8">
                              {[
                                { label: "Mémorisation de marque", value: 78 },
                                { label: "Engagement visuel", value: 92 },
                                { label: "Conversion en magasin", value: 45 }
                              ].map((stat, i) => (
                                <div key={i} className="bg-white bg-opacity-5 p-4 rounded-lg backdrop-blur-sm">
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="text-white text-opacity-90">{stat.label}</span>
                                    <span className="text-white font-bold">{stat.value}%</span>
                                  </div>
                                  <div className="h-2 bg-white bg-opacity-10 rounded-full overflow-hidden">
                                    <motion.div 
                                      className="h-full bg-gradient-to-r from-[#E84E0E] to-orange-500 rounded-full"
                                      initial={{ width: 0 }}
                                      whileInView={{ width: `${stat.value}%` }}
                                      transition={{ duration: 1, delay: 0.5 + i * 0.2 }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            <div className="flex justify-between text-center">
                              <div>
                                <p className="text-3xl font-bold text-white">20+</p>
                                <p className="text-sm text-white text-opacity-70">Villes couvertes</p>
                              </div>
                              <div>
                                <p className="text-3xl font-bold text-white">5M+</p>
                                <p className="text-sm text-white text-opacity-70">Impressions/mois</p>
                              </div>
                              <div>
                                <p className="text-3xl font-bold text-white">15+</p>
                                <p className="text-sm text-white text-opacity-70">Formats pris en charge</p>
                              </div>
                            </div>
                          </motion.div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </section>
              
        {/* Autres sections seront ajoutées ici */}
      </div>
    );
  };
  
  export default PartenaireSection;